import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SystemRoleName } from '@evimo/types/enums';
import { useAuth } from '~/app/auth/context/AuthContext';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { AssetService } from '~/app/services/asset/asset.service';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import type { KmsBrowseTarget, KmsLibraryMode } from '../types';

const ASSETS_PER_PAGE = 10;
const DEFAULT_DOCUMENTS_PER_PAGE = 12;
const DOCUMENT_PAGE_SIZE_OPTIONS = [12, 24, 50];
/** Tenant where KMS uploads were stored in local/dev. Prefer over similarly named "EVIMO". */
const PREFERRED_KMS_ORG_ID = '02bfc958-4580-426d-9860-7460630817f0';

export function useKmsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'hitl' ? 'hitl' : 'library';

  const [libraryMode, setLibraryMode] =
    useState<KmsLibraryMode>('organization');
  const [organizationId, setOrganizationId] = useState('');
  const [browseTarget, setBrowseTarget] =
    useState<KmsBrowseTarget>('org-global');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetPage, setAssetPage] = useState(1);
  const [documentPage, setDocumentPage] = useState(1);
  const [documentPageSize, setDocumentPageSize] = useState(
    DEFAULT_DOCUMENTS_PER_PAGE,
  );
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<
    string | undefined
  >();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const deferredAssetSearch = useDeferredValue(assetSearch);
  const deferredDocumentSearch = useDeferredValue(documentSearch);

  const isPlatformLibrary = isSuperAdmin && libraryMode === 'platform';
  /** Null for platform library; tenant UUID otherwise. */
  const effectiveOrganizationId: string | null = isPlatformLibrary
    ? null
    : organizationId || null;

  const organizationsQuery = useQuery({
    queryKey: ['organizations', 'kms'],
    queryFn: () => OrganizationService.findAllOrganizations(),
    enabled: isSuperAdmin,
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    // Non–Super Admin: always lock to JWT tenant.
    if (!isSuperAdmin) {
      if (user.organizationId) {
        setOrganizationId(user.organizationId);
        setLibraryMode('organization');
      }
      return;
    }

    if (libraryMode !== 'organization' || organizationId) {
      return;
    }

    // Super Admin: use JWT org immediately (admin@evimo.fr → EVIMO CPO),
    // then fall back to preferred org from the orgs list.
    if (user.organizationId) {
      setOrganizationId(user.organizationId);
      return;
    }

    if (organizationsQuery.data && organizationsQuery.data.length > 0) {
      const preferred =
        organizationsQuery.data.find(
          (org) => org.id === PREFERRED_KMS_ORG_ID,
        ) ??
        organizationsQuery.data.find(
          (org) => org.name.trim().toLowerCase() === 'evimo cpo',
        ) ??
        organizationsQuery.data.find((org) => /\bcpo\b/i.test(org.name)) ??
        organizationsQuery.data[0];
      setOrganizationId(preferred.id);
    }
  }, [
    isSuperAdmin,
    libraryMode,
    organizationId,
    organizationsQuery.data,
    user,
  ]);

  const organizationOptions = useMemo(() => {
    const rows = [...(organizationsQuery.data ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    return rows.map((org) => ({
      value: org.id,
      label: org.name,
    }));
  }, [organizationsQuery.data]);

  const assetsQuery = useQuery({
    queryKey: ['kms', 'assets', organizationId, assetPage, deferredAssetSearch],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId,
        page: assetPage,
        limit: ASSETS_PER_PAGE,
        q: deferredAssetSearch || undefined,
      }),
    enabled: Boolean(organizationId) && !isPlatformLibrary,
  });

  const hitlCountQuery = useQuery({
    queryKey: ['kms', 'hitl-queue', 'badge', effectiveOrganizationId],
    queryFn: () =>
      KmsDocumentService.listHitlQueue({
        page: 1,
        limit: 1,
        organizationId: effectiveOrganizationId,
        isSuperAdmin,
      }),
    enabled: isPlatformLibrary || Boolean(organizationId),
    refetchInterval: 30_000,
  });

  const linkedDocumentsQuery = useQuery({
    queryKey: [
      'kms',
      'linked-documents',
      effectiveOrganizationId,
      browseTarget,
      documentPage,
      documentPageSize,
      deferredDocumentSearch,
      documentTypeFilter,
      statusFilter,
    ],
    queryFn: () =>
      KmsDocumentService.listLinkedDocuments({
        page: documentPage,
        limit: documentPageSize,
        organizationId: effectiveOrganizationId,
        // Organization-wide = all docs for the tenant (any type / status).
        // Asset browse = docs linked or scoped to that asset only.
        assetId: browseTarget === 'org-global' ? undefined : browseTarget,
        isSuperAdmin,
        q: deferredDocumentSearch.trim() || undefined,
        documentType: documentTypeFilter,
        status: statusFilter,
      }),
    enabled: isPlatformLibrary || Boolean(organizationId),
    refetchInterval: 15_000,
  });

  const onLinkedDocumentsPageChange = useCallback(
    (page: number, pageSize?: number) => {
      if (pageSize && pageSize !== documentPageSize) {
        setDocumentPageSize(pageSize);
        setDocumentPage(1);
        return;
      }
      setDocumentPage(page);
    },
    [documentPageSize],
  );

  const onTabChange = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const onLibraryModeChange = useCallback((mode: KmsLibraryMode) => {
    setLibraryMode(mode);
    setBrowseTarget('org-global');
    setAssetPage(1);
    setAssetSearch('');
    setDocumentSearch('');
    setDocumentTypeFilter(undefined);
    setStatusFilter(undefined);
    setDocumentPage(1);
    if (mode === 'platform') {
      setIsUploadModalOpen(false);
    }
  }, []);

  const onOrganizationIdChange = useCallback((nextOrgId: string) => {
    setOrganizationId(nextOrgId);
    setBrowseTarget('org-global');
    setAssetPage(1);
    setAssetSearch('');
    setDocumentSearch('');
    setDocumentTypeFilter(undefined);
    setStatusFilter(undefined);
    setDocumentPage(1);
  }, []);

  const onBrowseTargetChange = useCallback((target: KmsBrowseTarget) => {
    setBrowseTarget(target);
    setDocumentPage(1);
  }, []);

  const onDocumentSearchChange = useCallback((value: string) => {
    setDocumentSearch(value);
    setDocumentPage(1);
  }, []);

  const onDocumentTypeFilterChange = useCallback(
    (value: string | undefined) => {
      setDocumentTypeFilter(value);
      setDocumentPage(1);
    },
    [],
  );

  const onStatusFilterChange = useCallback((value: string | undefined) => {
    setStatusFilter(value);
    setDocumentPage(1);
  }, []);

  const selectedOrganizationName = useMemo(() => {
    if (isPlatformLibrary) {
      return null;
    }
    return (
      organizationOptions.find((org) => org.value === organizationId)?.label ??
      null
    );
  }, [isPlatformLibrary, organizationId, organizationOptions]);

  const selectedAsset = useMemo(() => {
    if (browseTarget === 'org-global') {
      return null;
    }
    return (
      (assetsQuery.data?.data ?? []).find((a) => a.id === browseTarget) ?? null
    );
  }, [assetsQuery.data?.data, browseTarget]);

  return useMemo(
    () => ({
      activeTab,
      onTabChange,
      hitlBadgeCount: hitlCountQuery.data?.total ?? 0,
      isSuperAdmin,
      isPlatformLibrary,
      libraryMode,
      onLibraryModeChange,
      organizationId,
      effectiveOrganizationId,
      selectedOrganizationName,
      organizations: organizationsQuery.data ?? [],
      organizationOptions,
      onOrganizationIdChange,
      browseTarget,
      onBrowseTargetChange,
      selectedAsset,
      linkedDocuments: linkedDocumentsQuery.data?.data ?? [],
      linkedDocumentsTotal: linkedDocumentsQuery.data?.total ?? 0,
      linkedDocumentsPage: documentPage,
      linkedDocumentsPageSize: documentPageSize,
      linkedDocumentsPageSizeOptions: DOCUMENT_PAGE_SIZE_OPTIONS,
      onLinkedDocumentsPageChange,
      documentSearch,
      onDocumentSearchChange,
      documentTypeFilter,
      onDocumentTypeFilterChange,
      statusFilter,
      onStatusFilterChange,
      isLoadingLinkedDocuments: linkedDocumentsQuery.isLoading,
      linkedDocumentsError:
        linkedDocumentsQuery.error instanceof Error
          ? linkedDocumentsQuery.error.message
          : linkedDocumentsQuery.isError
            ? 'Failed to load documents'
            : null,
      assets: assetsQuery.data?.data ?? [],
      totalAssets: assetsQuery.data?.meta.total ?? 0,
      isLoadingAssets: assetsQuery.isLoading,
      assetPage,
      onAssetPageChange: setAssetPage,
      assetSearch,
      onAssetSearchChange: setAssetSearch,
      isUploadModalOpen,
      openUploadModal: () => setIsUploadModalOpen(true),
      closeUploadModal: () => setIsUploadModalOpen(false),
    }),
    [
      activeTab,
      assetPage,
      assetSearch,
      assetsQuery.data?.data,
      assetsQuery.data?.meta.total,
      assetsQuery.isLoading,
      browseTarget,
      documentPage,
      documentPageSize,
      documentSearch,
      documentTypeFilter,
      statusFilter,
      deferredDocumentSearch,
      effectiveOrganizationId,
      hitlCountQuery.data?.total,
      isPlatformLibrary,
      isSuperAdmin,
      isUploadModalOpen,
      linkedDocumentsQuery.data?.data,
      linkedDocumentsQuery.data?.total,
      linkedDocumentsQuery.error,
      linkedDocumentsQuery.isError,
      linkedDocumentsQuery.isLoading,
      libraryMode,
      onBrowseTargetChange,
      onDocumentSearchChange,
      onDocumentTypeFilterChange,
      onStatusFilterChange,
      onLibraryModeChange,
      onLinkedDocumentsPageChange,
      onOrganizationIdChange,
      onTabChange,
      organizationId,
      organizationOptions,
      organizationsQuery.data,
      selectedAsset,
      selectedOrganizationName,
    ],
  );
}
